import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from './Button';

interface AccessGateProps {
  children: React.ReactNode;
}

/**
 * Wraps any page that requires an active subscription or valid trial.
 * If the user's trial has expired or subscription is cancelled/expired,
 * they see a paywall instead of the page content.
 */
export function AccessGate({ children }: AccessGateProps) {
  const { profile } = useAuth();
  const navigate    = useNavigate();

  if (!profile) return null;

  const { subscriptionStatus, trialEndsAt } = profile;

  // Active subscribers always pass through
  if (subscriptionStatus === 'active') {
    return <>{children}</>;
  }

  // Trial users: check if still within trial window
  if (subscriptionStatus === 'trial' && trialEndsAt) {
    const end      = (trialEndsAt as any).toDate ? (trialEndsAt as any).toDate() : new Date(trialEndsAt as any);
    const stillValid = end.getTime() > Date.now();
    if (stillValid) return <>{children}</>;
  }

  // Admins always pass through
  if (profile.isAdmin) return <>{children}</>;

  // Everyone else sees the paywall
  const isExpiredTrial = subscriptionStatus === 'trial' || subscriptionStatus === 'expired';
  const isCancelled    = subscriptionStatus === 'cancelled';

  return (
    <div style={{
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      minHeight:      '60vh',
      padding:        '40px 24px',
      textAlign:      'center',
    }}>
      <div style={{
        maxWidth:     420,
        background:   'rgba(255,255,255,0.03)',
        border:       '1px solid rgba(201,168,76,0.2)',
        borderRadius: 12,
        padding:      '40px 32px',
      }}>
        <div style={{ fontSize: '2rem', marginBottom: 16 }}>
          {isExpiredTrial ? '⏱' : '🔒'}
        </div>

        <h2 style={{
          fontFamily:    'var(--font-display)',
          fontSize:      '1.3rem',
          fontWeight:    700,
          color:         'var(--chalk)',
          letterSpacing: '-0.02em',
          marginBottom:  8,
        }}>
          {isExpiredTrial ? 'Your free trial has ended' : 'Subscription required'}
        </h2>

        <p style={{
          fontSize:     '0.85rem',
          color:        'var(--chalk-dim)',
          lineHeight:   1.6,
          marginBottom: 28,
        }}>
          {isExpiredTrial
            ? 'Subscribe to 147coach Pro to continue tracking your sessions, accessing drills, and monitoring your progress.'
            : isCancelled
            ? 'Your subscription was cancelled. Resubscribe to regain full access.'
            : 'A Pro subscription is required to access this feature.'}
        </p>

        <Button fullWidth onClick={() => navigate('/subscribe')}>
          {isCancelled ? 'Resubscribe — €6.50/mo' : 'Subscribe — €6.50/mo'}
        </Button>

        <p style={{
          marginTop:     12,
          fontSize:      '0.65rem',
          color:         'var(--chalk-faint)',
          fontFamily:    'var(--font-mono)',
          letterSpacing: '0.05em',
        }}>
          Secure checkout via Stripe · Cancel anytime
        </p>
      </div>
    </div>
  );
}